import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { toast } from 'react-toastify';
import Modal from '../../../components/Modal';
import Label from '../../../components/label';
import { updatePropertyRequestStatus } from '../../../network/apis';

export default function PropertyRequestDetailsModal({
  open,
  handleClose,
  request,
  areaLookupsMap = {},
  budgetLookupsMap = {},
  roomCountLookupsMap = {},
  onStatusUpdated,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setComment('');
  }, [request?.id, open]);

  if (!request) return null;

  const isCompleted = request?.status === 'COMPLETED';
  const isResidential = request?.purpose === 'RESIDENTIAL';
  const isCommercial = request?.purpose === 'COMMERCIAL';

  const formattedArea =
    areaLookupsMap[request?.requiredArea] ||
    request?.requiredArea ||
    '---';

  const formattedBudget =
    budgetLookupsMap[request?.budget] ||
    request?.budget ||
    '---';

  const FALLBACK_ROOM_COUNT = {
    ONE: '1',
    TWO: '2',
    THREE: '3',
    FOUR: '4',
    FIVE: '5',
    SIX: '6',
    SEVEN: '7',
  };

  const formattedRoomCount =
    roomCountLookupsMap[request?.requiredRoomCount] ||
    FALLBACK_ROOM_COUNT[request?.requiredRoomCount] ||
    request?.requiredRoomCount ||
    '---';

  const displayCreationDate = request?.creationDate
    ? new Date(request.creationDate).toLocaleString(isRtl ? 'ar-SA' : 'en-US')
    : '---';

  const displayUpdateDate = request?.lastUpdateDate
    ? new Date(request.lastUpdateDate).toLocaleString(isRtl ? 'ar-SA' : 'en-US')
    : '---';

  const handleMakeContacted = async () => {
    if (!request?.id) return;
    setSubmitting(true);
    try {
      const payload = {
        id: String(request.id),
        comment: comment || '',
      };
      const res = await updatePropertyRequestStatus(payload);
      if (res?.data?.STATUS === 'FAILED' || res?.data?.result === 'Fail') {
        toast.error(res?.data?.MESSAGE || res?.data?.message || t('propertyRequests.statusUpdatedError'));
      } else {
        toast.success(res?.data?.MESSAGE || res?.data?.message || t('propertyRequests.statusUpdatedSuccess'));
        if (onStatusUpdated) {
          onStatusUpdated();
        }
        handleClose();
      }
    } catch (err) {
      console.error('Error updating property request status:', err);
      toast.error(
        err?.response?.data?.MESSAGE ||
        err?.response?.data?.message ||
        t('propertyRequests.statusUpdatedError')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      handleClose={handleClose}
      title={t('propertyRequests.details')}
      width="680px"
    >
      <Box sx={{ mt: 1 }}>
        {/* Top Header Card: Request Number & Status */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            mb: 2.5,
            borderRadius: '12px',
            backgroundColor: '#F8FAFC',
            border: '1px solid #E2E8F0',
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {t('propertyRequests.requestNumber')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F172A' }}>
              #{request?.requestNumber || request?.id}
            </Typography>
          </Box>

          <Label
            variant="filled"
            color={isCompleted ? 'success' : 'warning'}
            sx={{ px: 2, py: 1, fontSize: '0.875rem', fontWeight: 600, borderRadius: '8px' }}
          >
            {isCompleted
              ? t('propertyRequests.completed')
              : t('propertyRequests.pending')}
          </Label>
        </Box>

        {/* Update Status Card for PENDING status */}
        {!isCompleted && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              backgroundColor: '#F0FDF4',
              borderRadius: '12px',
              border: '1px solid #BBF7D0',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: '#166534',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CheckCircleOutlineIcon fontSize="small" />
              {t('propertyRequests.updateStatus')} - {t('propertyRequests.markAsContacted')}
            </Typography>

            <Typography variant="caption" sx={{ color: '#15803D', display: 'block', mb: 1 }}>
              {t('propertyRequests.contactedComment')}
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('propertyRequests.commentPlaceholder')}
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
                mb: 2,
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton
                variant="contained"
                loading={submitting}
                onClick={handleMakeContacted}
                startIcon={<CheckCircleOutlineIcon />}
                sx={{
                  backgroundColor: '#0CAF60',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  px: 3,
                  py: 1,
                  fontWeight: 700,
                  gap: 1, // Space between icon and text
                  '&:hover': {
                    backgroundColor: '#0A9451',
                  },
                }}
              >
                {t('propertyRequests.markAsContacted')}
              </LoadingButton>
            </Box>
          </Paper>
        )}

        {/* Section 1: Requester Information */}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: '#1E293B', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          👤 {t('propertyRequests.requesterInfo')}
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: '#F8FAFC',
            borderRadius: '12px',
            border: '1px solid #E2E8F0',
          }}
        >
          <Grid container spacing={2}>
            {
              request?.user?.fullName?.trim() !== "" &&
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('propertyRequests.fullName')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5 }}>
                  {request?.user?.fullName?.trim() || '---'}
                </Typography>
              </Grid>
            }
            {
              request?.user?.phone &&
              < Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('propertyRequests.phone')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5, direction: 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
                  {request?.user?.phone || '---'}
                </Typography>
              </Grid>
            }

            {request?.user?.nationalId && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('propertyRequests.nationalId')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5 }}>
                  {request.user.nationalId}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Section 2: Property Request Details */}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: '#1E293B', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          🏢 {t('propertyRequests.requestDetails')}
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: '#F8FAFC',
            borderRadius: '12px',
            border: '1px solid #E2E8F0',
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('propertyRequests.requestType')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5 }}>
                {isResidential
                  ? t('propertyRequests.residential')
                  : isCommercial
                    ? t('propertyRequests.commercial')
                    : '---'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('propertyRequests.destination')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5 }}>
                {request?.coordinate?.name || '---'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t(isResidential ? 'propertyRequests.unitType' : 'propertyRequests.propertyType')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5 }}>
                {request?.unitType?.name || '---'}
              </Typography>
            </Grid>

            {isResidential && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('propertyRequests.bedroomCount')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5 }}>
                  {formattedRoomCount}
                </Typography>
              </Grid>
            )}

            {isCommercial && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('propertyRequests.requiredArea')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#0284C7', mt: 0.5 }}>
                  {formattedArea}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t(isCommercial ? 'propertyRequests.pricePerMeterBudget' : 'propertyRequests.budget')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#16A34A', mt: 0.5 }}>
                {formattedBudget}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('propertyRequests.notes')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: '#334155',
                  mt: 0.5,
                  p: 1.5,
                  backgroundColor: '#FFFFFF',
                  borderRadius: '8px',
                  border: '1px dashed #CBD5E1',
                }}
              >
                {request?.notes || '---'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Section 3: Dates */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('propertyRequests.creationDate')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
              {displayCreationDate}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('propertyRequests.lastUpdateDate')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
              {displayUpdateDate}
            </Typography>
          </Grid>
        </Grid>

        {/* Close Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              backgroundColor: '#1E293B',
              color: '#FFFFFF',
              borderRadius: '10px',
              px: 4,
              py: 1,
              '&:hover': {
                backgroundColor: '#0F172A',
              },
            }}
          >
            {t('propertyRequests.close')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

PropertyRequestDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  request: PropTypes.object,
  areaLookupsMap: PropTypes.object,
  budgetLookupsMap: PropTypes.object,
  roomCountLookupsMap: PropTypes.object,
  onStatusUpdated: PropTypes.func,
};
